import { formatCurrency } from '@angular/common';
import { AfterContentInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AccountModificationFlag,
  DelinquentStatus,
  FeeType,
  LiquidityEvent,
  LoanInfoDto,
  LoanStatus,
  PaymentFrequencyLabel,
  ProductCode
} from 'common';
import { CustomerData } from '../../customer/customer.model';
import { readWrite } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { UserData } from '../../user/user.model';
import {
  AccountAmortizationInfo,
  AccountStatementsData,
  AccountWithRenewalData
} from '../account.model';
import { AccountService } from '../account.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'ifb-account-app-bar-expanded',
    templateUrl: './account-app-bar-expanded.component.html',
    styleUrls: ['./account-app-bar-expanded.component.scss'],
    standalone: false
})
export class AccountAppBarExpandedComponent implements OnInit, AfterContentInit {

  fundsAvailable: string;
  fundsAvailableFactor: number;
  data: AccountAppBarExpandedComponentData;
  paymentFrequency: string;
  accountStatus: LoanStatus;
  accountInfo: LoanInfoDto;
  accessNPLEconomics: boolean;
  
  readonly ProductCode = ProductCode;
  readonly FeeType = FeeType;
  readonly LoanStatus = LoanStatus;
  readonly DelinquentStatus = DelinquentStatus;

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private userPermissionService: UserPermissionService
  ) { }

  ngOnInit() {
  this.route.data.pipe(untilDestroyed(this)).subscribe(this.dataSet.bind(this));

    this.userPermissionService.granted([readWrite('servicing-account-npl-economics')])
      .pipe(untilDestroyed(this))  
      .subscribe(res => this.accessNPLEconomics = res);
  }

  ngAfterContentInit(): void {
  this.accountService.obsCurrentLoan.pipe(untilDestroyed(this))
      .subscribe(res => {
        if (res) {
          this.refreshData(res);
        }
      });
  }

  refreshData(accountData): void {
    this.accountService.getInfo(accountData.id).subscribe(accountInfoData => {
      this.dataSet({
        account: accountData,
        accountInfo: accountInfoData
      });
    });
  }

  dataSet(data: AccountAppBarExpandedComponentData) {
    this.data = { ...data };
    this.accountStatus = this.data.account ? this.data.account.status : null;
    this.accountInfo = this.data.accountInfo;

    if (data.account && data.account.offer) {
      this.paymentFrequency = PaymentFrequencyLabel.get(data.account.offer.paymentFrequency);
    }

    this.fundsAvailable = formatCurrency(this.data.accountInfo?.availableFunds, 'en_US', '$', undefined, '1.0-0');
    this.fundsAvailableFactor = this.data.accountInfo?.availableFunds / this.data.accountInfo?.creditLimit;

    if(data.account.statementGeneration) {
      this.data.accountStatements = {
        alligatorMatch: data.account.alligatorMatch,
        loanId: data.account.id,
        ...data.account.statementGeneration
      }
    }
  }

  getOutstandingBalance(): number {
    return this.accountService.getOutstandingBalance(this.data.account);
  }

  showModificationChipState() {
    return this.data.account.modificationFlag !== null
      && this.data.account.modificationFlag !== AccountModificationFlag.None;
  }

  isNPLLoan(): boolean {
    return this.accountService.isNPLLoan(this.data?.account?.status);
  }

}

export interface AccountAppBarExpandedComponentData {
  account: AccountWithRenewalData;
  accountInfo: LoanInfoDto;
  accountAmortizationInfo?: AccountAmortizationInfo[];
  userCurrent?: UserData;
  owners?: CustomerData[];
  liquidityEvent?: LiquidityEvent;
  accountStatements?: AccountStatementsData;
}
